import type React from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { CommitEntry } from '../types/wrap-offset'
import type { LayoutPanelTextWrapProps } from '../components/layout-panel-text-wrap'

type UseCommitToTargetsArgs = {
  shapeManagerRef: React.RefObject<ShapeManagerRef | null> | undefined
  activeObjects: ShapeData[]
  setActiveObjects: (objs: ShapeData[]) => void
  onBatchShapesCommitted: LayoutPanelTextWrapProps['onBatchShapesCommitted']
}

/** Returns `commitToTargets`, which writes wrap entries to the manager, refreshes the patched
 *  objects in the activeObjects context and reports them as one undoable batch. */
export function useCommitToTargets({
  shapeManagerRef,
  activeObjects,
  setActiveObjects,
  onBatchShapesCommitted,
}: UseCommitToTargetsArgs) {
  /** Commits every target's own before/after patch in one batch — the multi-object generalization
   *  of the old single-target `commitToTarget`. With exactly one entry this produces exactly the
   *  same single-item `BatchShapeTransformItem[]` call the old code made, so single-selection
   *  undo/persistence is unchanged. Preserves every OTHER currently-selected (but not
   *  wrap-relevant, or unaffected-by-this-commit) object already in context — only the entries
   *  actually committed here are patched, matching the "don't collapse a multi-selection" rule the
   *  rest of the app already follows for `setActiveObjects`. Shapes and text frames share one
   *  `ShapeManager` now, so patching here re-renders the whole tree on the same tick — no separate
   *  cross-manager wrap-sync notification is needed (unlike the old two-manager world). */
  const commitToTargets = (entries: CommitEntry[]) => {
    const live = entries.filter(({ after }) => Object.keys(after).length > 0)

    if (live.length === 0) return

    const mgr = shapeManagerRef?.current

    if (!mgr) return

    const shapeItems: BatchShapeTransformItem[] = []
    const updatedShapesById = new Map<string, ShapeData>()

    for (const { target, before, after } of live) {
      mgr.updateShape(target.id, after)

      const updated = mgr.getShapes().find((s) => s.id === target.id)

      if (updated) updatedShapesById.set(target.id, updated)
      shapeItems.push({ shapeId: target.id, before, after, shape: updated })
    }

    if (updatedShapesById.size > 0) {
      setActiveObjects(activeObjects.map((o) => updatedShapesById.get(o.id) ?? o))
    }

    if (shapeItems.length > 0) {
      onBatchShapesCommitted?.(shapeItems, 'update')
    }
  }

  return commitToTargets
}
